import { useState, useEffect } from 'react';
import { LockClosedIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../../components/mobile/BottomNav';


import sub2en from '../../assets/sub/Gold/Technical Analysis Indicators/1. Technical Indicators_en.vtt';
import sub2ru from '../../assets/sub/Gold/Technical Analysis Indicators/1. Technical Indicators_ru.vtt';
import sub3en from '../../assets/sub/Gold/Technical Analysis Indicators/2. Simple Moving Averages_en.vtt';
import sub3ru from '../../assets/sub/Gold/Technical Analysis Indicators/2. Simple Moving Averages_ru.vtt';
import sub4en from '../../assets/sub/Gold/Technical Analysis Indicators/3. Moving Averages Crossovers_en.vtt';
import sub4ru from '../../assets/sub/Gold/Technical Analysis Indicators/3. Moving Averages Crossovers_ru.vtt';
import sub5en from '../../assets/sub/Gold/Technical Analysis Indicators/4. Bollinger Bands_en.vtt';
import sub5ru from '../../assets/sub/Gold/Technical Analysis Indicators/4. Bollinger Bands_ru.vtt';
import sub6en from '../../assets/sub/Gold/Technical Analysis Indicators/5. Relative Strength Index (RSI)_en.vtt';
import sub6ru from '../../assets/sub/Gold/Technical Analysis Indicators/5. Relative Strength Index (RSI)_ru.vtt';
import sub7en from '../../assets/sub/Gold/Technical Analysis Indicators/6. Fibonacci_en.vtt';
import sub7ru from '../../assets/sub/Gold/Technical Analysis Indicators/6. Fibonacci_ru.vtt';

const videoLessons = [
  { name: '1. Technical Indicators(1).webm', title: '2. Technical Indicators', subtitles: { en: sub2en, ru: sub2ru } },
  { name: '2. Simple Moving Averages(1).webm', title: '3. Simple Moving Averages', subtitles: { en: sub3en, ru: sub3ru } },
  { name: '3. Moving Averages Crossovers(1).webm', title: '4. Moving Averages Crossovers', subtitles: { en: sub4en, ru: sub4ru } },
  { name: '4. Bollinger Bands(1).webm', title: '5. Bollinger Bands', subtitles: { en: sub5en, ru: sub5ru } },
  { name: '5. Relative Strength Index (RSI)(1).webm', title: '6. Relative Strength Index (RSI)', subtitles: { en: sub6en, ru: sub6ru } },
  { name: '6. Fibonacci(1).webm', title: '7. Fibonacci', subtitles: { en: sub7en, ru: sub7ru } },
];

function TechnicalAnalysis() {
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const response = await fetch('https://api.maxprofitacademy.com/user', {
          method: 'GET',
          headers: { 'token': JSON.parse(token) }
        });
        const data = await response.json();
        if (data.status === 200) {
          setUserData(data.data);
        }
      } catch (error) {
        //
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const hasValidCourse = userData?.course && ['GOLD', 'SILVER', 'PLATINUM'].includes(userData.course.toUpperCase());

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!hasValidCourse) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <LockClosedIcon className="h-16 w-16 text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Нет доступа к видеоурокам</h2>
        <p className="text-gray-600">Для доступа к видеоурокам активируйте один из курсов (SILVER, GOLD, PLATINUM)</p>
        <BottomNav />
      </div>
    );
  }

  const currentVideo = videoLessons[selectedIdx];

  return (
    <div className="min-h-screen pb-20">
      <div className="flex items-center mb-6 px-4 pt-6">
        <button onClick={() => navigate(-1)} className="flex items-center text-primary font-semibold hover:underline">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Назад к разделам
        </button>
      </div>
      <div className="flex flex-col lg:flex-row gap-8 px-4">
        {/* Список уроков */}
        <div className="lg:w-1/3 w-full mb-4 lg:mb-0">
          <div className="bg-white rounded-2xl shadow-md p-4">
            <h2 className="text-lg font-bold mb-4">Список уроков</h2>
            <div className="flex flex-col gap-2 max-h-[70vh] overflow-y-auto">
              {videoLessons.map((video, idx) => (
                <button
                  key={video.name}
                  onClick={() => { setSelectedIdx(idx); setIsVideoLoading(true); }}
                  className={`text-left px-3 py-2 rounded-xl transition-colors flex items-center gap-2 ${
                    selectedIdx === idx ? 'bg-primary text-black font-bold' : 'hover:bg-gray-100 text-gray-800'
                  }`}
                >
                  <span className="font-mono w-6 text-right">{idx + 1}.</span>
                  <span className="truncate">{video.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Плеер и инфо */}
        <div className="flex-1 flex flex-col items-center">
          <div className="w-full bg-white rounded-2xl shadow-md p-4 flex flex-col items-center">
            <h1 className="text-2xl font-bold mb-4 text-center">{currentVideo.title}</h1>
            <div className="w-full aspect-video bg-black rounded-xl overflow-hidden relative">
              <video
                key={currentVideo.name}
                controls
                className="w-full h-full"
                onLoadedData={() => setIsVideoLoading(false)}
              >
                <source src={`https://cdn.maxprofitacademy.com/platinum/technical-analysis/${currentVideo.name}`} type="video/mp4" />
                {currentVideo.subtitles && (
                  <>
                    <track
                      label="Русский"
                      kind="subtitles"
                      srcLang="ru"
                      src={currentVideo.subtitles.ru}
                      default
                    />
                    <track
                      label="English"
                      kind="subtitles"
                      srcLang="en"
                      src={currentVideo.subtitles.en}
                    />
                  </>
                )}
                Ваш браузер не поддерживает видео.
              </video>
              {isVideoLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}

export default TechnicalAnalysis; 